import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WifiOff, RefreshCw, Radio, Sparkles, Heart, AlertCircle } from 'lucide-react';
import { romanticAudio } from '../utils/audio';

interface OnlineRealtimeGuardProps {
  children: React.ReactNode;
}

export const OnlineRealtimeGuard: React.FC<OnlineRealtimeGuardProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true;
  });
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  // Verificación activa de conectividad real
  const verifyRealConnection = useCallback(async (): Promise<boolean> => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return false;
    }
    try {
      // Petición liviana sin caché para confirmar que hay internet real y no un falso positivo
      const res = await fetch(`/manifest.json?_t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-store',
      });
      return res.ok || res.status < 500;
    } catch {
      return false;
    }
  }, []);

  const handleRetry = async () => {
    setIsChecking(true);
    setCheckError(null);
    try {
      const online = await verifyRealConnection();
      if (online) {
        setIsOnline(true);
        romanticAudio.playBloomSuccess();
      } else {
        setIsOnline(false);
        setCheckError('Aún no detectamos conexión a internet. Revisa tus datos móviles o Wi-Fi.');
        romanticAudio.playChime();
      }
    } catch {
      setIsOnline(false);
      setCheckError('No se pudo establecer conexión con el servidor.');
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    const handleOnline = async () => {
      const online = await verifyRealConnection();
      setIsOnline(online);
      if (online) {
        setCheckError(null);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Verificación inicial
    if (!navigator.onLine) {
      setIsOnline(false);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [verifyRealConnection]);

  return (
    <>
      {children}

      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/35 backdrop-blur-md"
            style={{ overscrollBehavior: 'contain' }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 15 }}
              transition={{ type: 'spring', damping: 25, stiffness: 320 }}
              className="w-full max-w-md bg-white border border-[#F0DDE4] rounded-3xl p-6 sm:p-8 shadow-[0_25px_60px_rgba(232,93,142,0.18)] text-center text-[#302B30] relative overflow-hidden"
            >
              {/* Resplandor ambiental de fondo */}
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-[#FFF4F7] rounded-full blur-3xl pointer-events-none" />

              {/* Insignia superior */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF4F7] border border-[#F0DDE4] text-[#E85D8E] text-[11px] font-bold tracking-wider uppercase mb-5">
                <Radio className="w-3.5 h-3.5 animate-pulse text-[#E85D8E]" />
                <span>Modo Exclusivo en Tiempo Real</span>
              </div>

              {/* Icono de sin conexión animado */}
              <div className="relative mx-auto w-20 h-20 mb-5 flex items-center justify-center">
                <div className="absolute inset-0 bg-[#E85D8E]/10 rounded-full animate-ping opacity-40" />
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-[#E85D8E] to-[#FF8DAF] flex items-center justify-center shadow-lg shadow-[#E85D8E]/25 border border-white">
                  <WifiOff className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Título y Explicación directa */}
              <h2 className="text-xl sm:text-2xl font-bold font-serif text-[#302B30] tracking-tight mb-2">
                Conexión a Internet Requerida
              </h2>

              <p className="text-sm text-[#77717A] leading-relaxed mb-5">
                Esta app funciona <strong className="text-[#E85D8E] font-semibold">únicamente en tiempo real</strong> y no de manera offline. Se requiere internet para que las cartas de amor, recuerdos, música y jardín se sincronicen al instante.
              </p>

              {/* Mensaje de error si falla el reintento */}
              {checkError && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2 text-left"
                >
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>{checkError}</span>
                </motion.div>
              )}

              {/* Botón de reintento */}
              <button
                type="button"
                id="btn-reintentar-conexion"
                onClick={handleRetry}
                disabled={isChecking}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#FF8DAF] hover:from-[#DF4D80] hover:to-[#FF7A9F] text-white font-bold text-sm shadow-md shadow-[#E85D8E]/25 active:scale-98 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
                <span>{isChecking ? 'Comprobando conexión...' : 'Reintentar Conexión Ahora'}</span>
              </button>

              {/* Pie con detalles */}
              <div className="mt-5 pt-4 border-t border-[#F0DDE4] flex items-center justify-center gap-2 text-[11px] text-[#77717A]">
                <Heart className="w-3 h-3 text-[#E85D8E] fill-[#E85D8E]" />
                <span>Nuestros Recuerdos • Sincronización en la nube</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
